import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useRazorpayScript } from "@/lib/useRazorpay";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Check, Crown, Sparkle, CreditCard, Wallet } from "@phosphor-icons/react";
import UpiPayModal from "@/components/UpiPayModal";

export default function PlanCard({ plan, featured, isCurrent, onActivated, paymentDisabled }) {
  const [modal, setModal] = useState(false);
  const [upiOpen, setUpiOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { user, refresh } = useAuth();
  const razorpayReady = useRazorpayScript();

  const startPay = async () => {
    if (plan.price === 0) {
      try {
        await api.post("/subscription/activate", { plan: plan.id });
        toast.success("Free plan activated");
        await refresh();
        onActivated?.();
      } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
      return;
    }
    setModal(true);
  };

  const payWithRazorpay = async () => {
    if (!razorpayReady) { toast.error("Payment gateway still loading…"); return; }
    setProcessing(true);
    try {
      const { data: order } = await api.post("/payments/razorpay/order", { plan: plan.id });
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "WR Rishtey",
        description: `${plan.name} membership`,
        order_id: order.order_id,
        prefill: { name: user?.name, email: user?.email, contact: user?.phone || "" },
        theme: { color: "#8C3A27" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              plan: plan.id,
            });
            toast.success(`${plan.name} activated!`);
            await refresh();
            setModal(false);
            onActivated?.();
          } catch (err) {
            toast.error(err.response?.data?.detail || "Verification failed");
          }
        },
        modal: {
          ondismiss: () => setProcessing(false),
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (r) => {
        toast.error(`Payment failed: ${r.error?.description || "unknown"}`);
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start payment");
    } finally {
      setProcessing(false);
    }
  };

  const openUpi = () => { setModal(false); setUpiOpen(true); };

  return (
    <div
      className={`p-8 lg:p-10 flex flex-col justify-between ${featured ? "bg-brand-ink text-white" : "bg-white"}`}
      data-testid={`plan-card-${plan.id}`}
    >
      <div>
        <div className="flex items-center gap-2">
          {plan.id === "platinum" && <Crown size={18} weight="duotone" className="text-brand-gold" />}
          {featured && <Sparkle size={18} weight="duotone" className="text-brand-gold" />}
          <div className={`label-eyebrow ${featured ? "text-brand-gold" : "text-brand-terracotta"}`}>{plan.name}</div>
        </div>
        <div className="mt-6 flex items-baseline gap-1">
          <span className={`font-serif text-5xl tracking-tighter ${featured ? "text-white" : "text-brand-ink"}`}>
            {plan.price === 0 ? "Free" : `₹${plan.price.toLocaleString("en-IN")}`}
          </span>
        </div>
        <div className={`mt-1 text-[11px] uppercase tracking-widest ${featured ? "text-white/60" : "text-brand-muted"}`}>
          {plan.duration_days ? `${plan.duration_days} days · one-time` : "one-time"}
        </div>
        <ul className={`mt-6 space-y-2.5 ${featured ? "text-white/85" : "text-brand-ink"}`}>
          {plan.features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-xs leading-snug">
              <Check size={14} weight="bold" className={featured ? "text-brand-gold mt-0.5 flex-shrink-0" : "text-brand-terracotta mt-0.5 flex-shrink-0"} />
              {f}
            </li>
          ))}
        </ul>
      </div>
      <Button
        disabled={isCurrent || (paymentDisabled && plan.price > 0)}
        onClick={startPay}
        data-testid={`activate-${plan.id}`}
        className={`mt-8 rounded-full w-full h-11 ${featured ? "bg-brand-gold text-brand-ink hover:bg-brand-gold/90" : "bg-brand-terracotta text-white hover:bg-brand-terracotta/90"}`}
      >
        {isCurrent ? "Current plan" : plan.price === 0 ? "Start free" : paymentDisabled ? "Accept terms below" : "Choose plan"}
      </Button>

      {/* Payment method chooser */}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-md bg-white border-brand-line" data-testid="pay-method-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl tracking-tight text-brand-ink">
              Choose payment method
            </DialogTitle>
            <DialogDescription className="text-brand-muted">
              <span className="font-semibold text-brand-ink">{plan.name}</span> — ₹{plan.price.toLocaleString("en-IN")} · {plan.duration_days || 30} days
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <button
              type="button"
              onClick={payWithRazorpay}
              disabled={processing}
              data-testid="pay-razorpay-btn"
              className="w-full flex items-start gap-4 p-5 border border-brand-line hover:border-brand-terracotta hover:bg-brand-bone transition-colors text-left"
            >
              <CreditCard size={26} weight="duotone" className="text-brand-terracotta mt-1" />
              <div className="flex-1">
                <div className="font-serif text-xl text-brand-ink">Razorpay checkout</div>
                <div className="text-sm text-brand-muted">Cards, Netbanking, Wallets & UPI apps</div>
              </div>
              <span className="text-brand-terracotta text-sm mt-2">→</span>
            </button>
            <button
              type="button"
              onClick={openUpi}
              data-testid="pay-upi-btn"
              className="w-full flex items-start gap-4 p-5 border border-brand-line hover:border-brand-terracotta hover:bg-brand-bone transition-colors text-left"
            >
              <Wallet size={26} weight="duotone" className="text-brand-terracotta mt-1" />
              <div className="flex-1">
                <div className="font-serif text-xl text-brand-ink">Direct UPI / G-Pay</div>
                <div className="text-sm text-brand-muted">Scan our QR or pay to our UPI ID directly</div>
              </div>
              <span className="text-brand-terracotta text-sm mt-2">→</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <UpiPayModal
        open={upiOpen}
        onOpenChange={setUpiOpen}
        plan={plan}
        onSubmitted={() => onActivated?.()}
      />
    </div>
  );
}
